import { getResponseActiveDuration } from '../../lib/response-table-summary';
import { ButtonContent } from '../ui/ButtonContent';
import { InsightText } from './report/InsightText';
import { useSavedReport } from './report/useSavedReport';
import { ResponseFiles } from './ResponseFiles';
import { ResponseAnswerTree } from './ResponseAnswerTree';
import { StoredResponseAnswer } from './StoredResponseAnswer';
import { getExploreResponseExchanges } from '../../lib/explore-response-exchanges';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSupabase } from '../../lib/SupabaseProvider';
import { highlightTurn } from '../../lib/utils/transcript/highlight-turn';
import { turnKeyForTurn } from '../../lib/transcript-timing';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import {
  updateResponseIntervieweeData, listNestedResponseLinks,
} from '../../services/interviewService';
import { translateInterview } from '../../services/geminiService';
import type { InterviewSession, InterviewData, ConversationTurn } from '../../types';
import { getLanguageName, DEFAULT_OUTPUT_LANGUAGE } from '../../lib/languages';
import { getResponseDisplayStatus, getResponseEmail } from '../../lib/admin-utils';
import { formatResponseTranscript, getResponseCompletion, getStructuredResponseAnswers, getStructuredResponseGroups, type NestedResponseLink, type StructuredResponseAnswer } from '../../lib/response-completion';
import { safeHttpUrl } from '../../lib/security/safe-http-url';
import { VideoTranscriptViewer } from './VideoTranscriptViewer';
import { useTranscriptTimings } from '../hooks/useTranscriptTimings';
import { CaseStudyGenerator } from './CaseStudyGenerator';
import { InterviewReport } from './report/InterviewReport';
import { CampaignAskBox } from './CampaignAskBox';
import { ConversationProgressCard } from './ConversationProgressCard';
import Empty from '../common/Empty';
import { printReport, type InterviewContext as PrintContext, type CapturedCharts } from './report/printReport';
import { StatusBadge, Skeleton } from '../ui';
import { AnimatedPresence } from '../ui/AnimatedPresence';
import { formatDate } from '../../lib/admin-utils';
import { IconPencil, IconLink, IconChevronDown } from '@tabler/icons-react';
import { Modal } from '../ui';
import { Tooltip } from '../Tooltip';
import { useOrg } from '../../lib/OrgProvider';
import { useConversationProgress } from '../hooks/useConversationProgress';

interface LoadedResponseDetailViewProps {
  /** Restore recipe discovery when returning from its authoring page. */
  focusRecipes?: boolean;
  sessionId: string;
  response: InterviewData;
  session: InterviewSession;
  /** Response selector and arrows share the response toolbar. */
  navigation?: React.ReactNode;
  /** Called when report availability changes, passing the download function or null. */
  onReportReady?: (downloadFn: (() => void) | null) => void;
  /**
   * Deep-link target: scroll the transcript to this canonical (all-turns) index.
   * Set by an ask-the-campaign citation clicked in the campaign overview.
   */
  focusTurnIndex?: number;
  /** Bumped each time a deep-link is requested, so repeated jumps re-fire. */
  focusTurnKey?: number;
}

/**
 * Full detail view for a single campaign response.
 * The response key scopes the reader, transcript and pending edits to one respondent.
 */
const LoadedResponseDetailView: React.FC<LoadedResponseDetailViewProps> = ({
  focusRecipes = false,
  sessionId,
  response: initialResponse,
  session,
  navigation,
  onReportReady,
  focusTurnIndex,
  focusTurnKey,
}) => {
  const { session: authSession } = useSupabase();
  const { hasPermission } = useOrg();
  /** Editing interviewee details mutates the response row - gated on
   *  `interviews:edit`. Viewers see the pencil icon disabled with a
   *  tooltip instead of it vanishing (keeps the card layout consistent). */
  const canEditInterview = hasPermission('interviews:edit');

  const [interviewData, setInterviewData] = useState<InterviewData>(initialResponse);
  const guestEmail = getResponseEmail(interviewData);
  const hasRecording = Boolean(interviewData.video_segments?.length);
  // Call settings and typed conversation turns do not prove a call took place.
  const hasCall = hasRecording || Boolean(interviewData.duration_segments?.some(segment =>
    Number.isFinite(segment.start_ms) && Number.isFinite(segment.end_ms) && segment.end_ms > segment.start_ms,
  ));
  const hasVideoRecording = Boolean(interviewData.video_segments?.some(segment => segment.segment_kind !== 'audio'));
  // Word-accurate timing rows (Stage 2 forced alignment) for this response.
  // Empty in Stage 1: the viewer falls back to utterance-level mapping silently.
  const { timingRows } = useTranscriptTimings(hasCall ? interviewData.id : undefined);
  const {
    progress: conversationProgress,
    conversationTurns: currentConversationTurns,
    unavailable: conversationProgressUnavailable,
  } = useConversationProgress(
    interviewData.id,
    interviewData.conversation_progress,
    interviewData.conversation_turns,
    {
      conversation_progress_version: interviewData.conversation_progress_version,
      conversation_progress_revision: interviewData.conversation_progress_revision,
      schema_fingerprint: interviewData.schema_fingerprint,
      conversation_engine_mode: interviewData.conversation_engine_mode,
    },
  );
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const recipesRef = useRef<HTMLDivElement>(null);
  const [copiedTranscript, setCopiedTranscript] = useState(false);

  // Scroll-to-turn state (for report -> transcript navigation)
  const [scrollToTurnIndex, setScrollToTurnIndex] = useState<number | null>(null);

  // Translation state
  const [translating, setTranslating] = useState(false);
  const [targetLanguage] = useState(DEFAULT_OUTPUT_LANGUAGE);
  const [showTranslated, setShowTranslated] = useState(false);
  const [translationError, setTranslationError] = useState<string | null>(null);

  // Interviewee edit state
  const [editingInterviewee, setEditingInterviewee] = useState(false);
  const [intervieweeForm, setIntervieweeForm] = useState({
    interviewee_name: '',
    interviewee_email: '',
    interviewee_job: '',
    interviewee_company: '',
    interviewee_linkedin: '',
    interviewee_company_url: '',
  });
  const [savingInterviewee, setSavingInterviewee] = useState(false);
  const [saveIntervieweeError, setSaveIntervieweeError] = useState<string | null>(null);
  const [recipeGuestError, setRecipeGuestError] = useState<string | null>(null);

  // Share the report between the summary, transcript citations and print action.
  const savedReport = useSavedReport(sessionId, initialResponse.id, getResponseCompletion(initialResponse).submitted);
  const { reportData } = savedReport;
  const hasReport = !!reportData;

  useEffect(() => {
    if (!focusRecipes) return undefined;
    const frame = requestAnimationFrame(() => recipesRef.current?.scrollIntoView({ block: 'start' }));
    return () => cancelAnimationFrame(frame);
  }, [focusRecipes, initialResponse.id]);

  const handleEditInterviewee = () => {
    setSaveIntervieweeError(null);
    setIntervieweeForm({
      interviewee_name: interviewData.interviewee_name || '',
      interviewee_email: guestEmail || '',
      interviewee_job: interviewData.interviewee_job || '',
      interviewee_company: interviewData.interviewee_company || '',
      interviewee_linkedin: interviewData.interviewee_linkedin || '',
      interviewee_company_url: interviewData.interviewee_company_url || '',
    });
    setEditingInterviewee(true);
  };

  const handleSaveInterviewee = async () => {
    setSavingInterviewee(true);
    try {
      await updateResponseIntervieweeData(interviewData.id, intervieweeForm);
      setInterviewData(prev => ({ ...prev, ...intervieweeForm }));
      setEditingInterviewee(false);
    } catch (err) {
      captureException(err instanceof Error ? err : String(err), 'ResponseDetailView.saveInterviewee', { sessionId, responseId: interviewData.id });
      setSaveIntervieweeError("Couldn't save. Try again.");
    } finally {
      setSavingInterviewee(false);
    }
  };

  /**
   * Exports the interview report as a printable HTML document.
   * Clones Recharts SVG chart elements from the DOM, adjusts their dimensions
   * for print layout, and serializes them into the print template.
   */
  const handleDownloadReport = useCallback(() => {
    if (!reportData) return;
    const context: PrintContext = {
      targetName: session.target_name,
      goal: session.goal,
      details: session.details || '',
      intervieweeName: interviewData.interviewee_name,
      intervieweeJob: interviewData.interviewee_job,
      intervieweeCompany: interviewData.interviewee_company,
    };

    const charts: CapturedCharts = {};
    const serializer = new XMLSerializer();

    const wrappers = document.querySelectorAll('.recharts-wrapper svg');
    if (wrappers[0]) {
      const svg = wrappers[0].cloneNode(true) as SVGSVGElement;
      svg.setAttribute('width', '700');
      svg.setAttribute('height', String(svg.getAttribute('height') || '300'));
      svg.removeAttribute('style');
      svg.querySelectorAll('text').forEach(t => { t.setAttribute('fill', '#6b7280'); });
      charts.sentimentJourneySvg = serializer.serializeToString(svg);
    }

    if (wrappers[1]) {
      const svg = wrappers[1].cloneNode(true) as SVGSVGElement;
      svg.setAttribute('width', '200');
      svg.setAttribute('height', '200');
      svg.removeAttribute('style');
      charts.sentimentOverviewSvg = serializer.serializeToString(svg);
    }

    printReport(reportData, context, charts);
  }, [reportData, session, interviewData]);

  // Notify parent when report download becomes available/unavailable
  useEffect(() => {
    if (!onReportReady) return;
    onReportReady(hasReport ? handleDownloadReport : null);
  }, [hasReport, handleDownloadReport, onReportReady]);

  const formatTranscriptText = (turns: Array<{ speaker: string; text: string; timestamp?: number }>) => {
    return formatResponseTranscript(turns, {
      ...interviewData, conversation_progress: conversationProgress ?? interviewData.conversation_progress,
    });
  };

  const handleCopyTranscript = async () => {
    if (!currentConversationTurns.length) return;

    const turnsToUse = (showTranslated && interviewData.translated_turns)
      ? interviewData.translated_turns
      : currentConversationTurns;

    const transcriptText = formatTranscriptText(turnsToUse);

    try {
      await navigator.clipboard.writeText(transcriptText);
      setCopiedTranscript(true);
      setTimeout(() => setCopiedTranscript(false), 2000);
    } catch (err) {
      captureException(err instanceof Error ? err : String(err), 'ResponseDetailView.copyTranscript', { sessionId, responseId: interviewData.id });
    }
  };

  const handleDownloadTranscript = () => {
    if (!currentConversationTurns.length && !getStructuredResponseAnswers(interviewData).length) return;

    const turnsToUse = (showTranslated && interviewData.translated_turns)
      ? interviewData.translated_turns
      : currentConversationTurns;

    const transcriptText = formatTranscriptText(turnsToUse);
    const suffix = showTranslated ? '-translated' : '';
    const responseLabel = interviewData.response_number ? `-r${interviewData.response_number}` : '';
    const filename = `transcript-${(session.target_name || 'call').replace(/\s+/g, '-')}-${sessionId.slice(0, 8)}${responseLabel}${suffix}.txt`;

    const blob = new Blob([transcriptText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleTranslate = async () => {
    if (!authSession?.access_token) return;

    setTranslating(true);
    setTranslationError(null);

    try {
      const result = await translateInterview(sessionId, targetLanguage, authSession.access_token);

      if (result.success && result.translated_turns) {
        setInterviewData(prev => ({
          ...prev,
          translated_turns: result.translated_turns as ConversationTurn[],
          translated_language: result.translated_language,
          translated_at: new Date().toISOString(),
          original_language: result.original_language,
        }));
        setShowTranslated(true);
      }
    } catch (err) {
      captureException(err instanceof Error ? err : String(err), 'ResponseDetailView.translateTranscript', { sessionId, responseId: interviewData.id, targetLanguage });
      setTranslationError(err instanceof Error ? err.message : 'Translation failed');
    } finally {
      setTranslating(false);
    }
  };

  /**
   * Navigate from report quote to the corresponding transcript turn.
   * The report provides a 1-based "user turn index" (counting only user turns),
   * but the transcript uses 0-based indices across all turn types (user, ai, system).
   * Falls back to using turnIndex directly if no matching user turn is found.
   */
  const handleNavigateToTranscript = (turnIndex: number, _timestamp?: number) => {
    if (!hasCall) return;
    const turns = interviewData.conversation_turns;
    let userCount = 0;
    for (let i = 0; i < turns.length; i++) {
      if (turns[i].speaker === 'user') {
        userCount++;
        if (userCount === turnIndex) {
          setScrollToTurnIndex(i);
          setTranscriptOpen(true);
          return;
        }
      }
    }
    setScrollToTurnIndex(turnIndex);
    setTranscriptOpen(true);
  };

  /**
   * Jump straight to a canonical (all-turns) transcript index. Ask-the-campaign
   * citations already carry the canonical turn index, so they bypass the
   * report-quote user-turn conversion in handleNavigateToTranscript.
   */
  const handleNavigateToTurn = (turnIndex: number) => {
    if (!hasCall) return;
    setScrollToTurnIndex(turnIndex);
    setTranscriptOpen(true);
  };

  /** Resolve stable progress evidence to the transcript's canonical turn index. */
  const handleNavigateToEvidenceTurn = (turnId: string) => {
    const turnIndex = currentConversationTurns.findIndex(
      (turn) => turnKeyForTurn(turn) === turnId,
    );
    if (turnIndex < 0) return;
    handleNavigateToTurn(turnIndex);
  };

  // Deep-link from a campaign-ask citation: focus the cited turn when the parent
  // asks (focusTurnKey changes on every request so a repeat jump re-fires).
  useEffect(() => {
    if (!hasCall || focusTurnIndex == null) return;
    setScrollToTurnIndex(focusTurnIndex);
    setTranscriptOpen(true);
  }, [focusTurnIndex, focusTurnKey, hasCall]);

  // Clear scrollToTurnIndex after 5 seconds
  useEffect(() => {
    if (scrollToTurnIndex == null) return undefined;
    const timer = setTimeout(() => setScrollToTurnIndex(null), 5000);
    return () => clearTimeout(timer);
  }, [scrollToTurnIndex]);

  // Scroll to a specific turn in the non-video transcript view.
  // Uses a 300ms delay to allow the transcript disclosure to render before querying
  // the element, then delegates scroll and highlight to highlightTurn.
  useEffect(() => {
    if (scrollToTurnIndex == null || !transcriptOpen) return undefined;
    let innerCleanup: (() => void) | undefined;
    const outerTimer = setTimeout(() => {
      const el = document.querySelector(`[data-turn-index="${scrollToTurnIndex}"]`);
      if (el) {
        innerCleanup = highlightTurn(el);
      }
    }, 300);
    return () => {
      clearTimeout(outerTimer);
      innerCleanup?.();
    };
  }, [scrollToTurnIndex, transcriptOpen]);

  const responseStatus = getResponseDisplayStatus(interviewData);
  const completion = getResponseCompletion({
    ...interviewData, conversation_progress: conversationProgress ?? interviewData.conversation_progress,
  });
  const isCompleted = completion.submitted;
  const [childLinks, setChildLinks] = useState<NestedResponseLink[]>([]);
  const hasNested = interviewData.conversation_config_snapshot?.sections?.some(section => section.blocks?.length);
  useEffect(() => {
    if (!hasNested || !isCompleted) return undefined;
    let current = true;
    void listNestedResponseLinks(interviewData.id).then(links => {
      if (current) setChildLinks(links);
    }).catch(error => captureException(error, 'ResponseDetailView.nestedResponseLinks', { responseId: interviewData.id }));
    return () => { current = false; };
  }, [interviewData.id, hasNested, isCompleted]);
  const answerGroups = getStructuredResponseGroups(interviewData, childLinks);
  const isInsufficient = responseStatus === 'insufficient';
  const showsConversationProgress = responseStatus === 'in_progress' || isCompleted || completion.multimodal;
  const hasUsefulResponseData = currentConversationTurns.length > 0
    || Object.keys(interviewData.form_answers ?? {}).length > 0
    || Boolean(interviewData.completed_at);

  // Ask launcher for this single conversation, sitting in the sticky right
  // sidebar above the Guest card. Keyed by the response id so the right thread
  // list loads for the right conversation.
  const askBox = (
    <CampaignAskBox
      key={interviewData.id}
      interviewId={sessionId}
      interviewDataIds={[interviewData.id]}
      placeholder="Ask about this conversation"
      onCitationClick={hasCall ? (citation) => handleNavigateToTurn(citation.turn_index) : undefined}
    />
  );

  // Stored answers follow recipes, ahead of the supporting analysis.
  const exploreExchanges = React.useMemo(() => getExploreResponseExchanges(interviewData.form_schema_snapshot, currentConversationTurns, conversationProgress),
    [interviewData.form_schema_snapshot, currentConversationTurns, conversationProgress]);
  const renderAnswers = (answers: StructuredResponseAnswer[]) => <dl className="space-y-5">{answers.map(answer =>
    <StoredResponseAnswer key={answer.fieldId} answer={answer} insights={reportData?.aiContext}
      field={interviewData.form_schema_snapshot?.find(field => field.id === answer.fieldId)} exchanges={exploreExchanges.get(answer.fieldId)} />
  )}</dl>;
  const responseResults = (
    <>
      {answerGroups.length > 0 && <section aria-label="Stored answers" className="bg-background/50 border border-border/50 rounded-2xl p-8">
        <div className="flex items-center justify-between gap-3 mb-5">
          <h3 className="text-sm font-medium text-foreground">Stored answers</h3>
          <button type="button" className="text-sm text-muted-foreground hover:text-foreground cursor-pointer underline underline-offset-4"
            onClick={handleDownloadTranscript}>Download answers</button>
        </div>
        <ResponseAnswerTree groups={answerGroups} childLinks={childLinks} renderAnswers={renderAnswers} />
      </section>}
      {interviewData.form_schema_snapshot?.some(field => field.type === 'file_upload') ? <ResponseFiles responseId={interviewData.id} token={authSession?.access_token} /> : null}
    </>
  );

  const recipes = (
    <div ref={recipesRef} className="min-w-0 scroll-mt-44 md:scroll-mt-28">
      {recipeGuestError && (
        <p className="text-sm text-danger">{recipeGuestError}</p>
      )}
      <CaseStudyGenerator
        interviewId={sessionId}
        responseId={interviewData.id}
        interviewStatus={isCompleted ? 'completed' : 'active'}
        hasTranscript={(interviewData.conversation_turns ?? []).some(turn => turn.speaker === 'user')}
        hasStoredAnswers={answerGroups.some(group => group.answers.length > 0)}
        intervieweeInfo={{
          interviewee_name: interviewData.interviewee_name,
          interviewee_job: interviewData.interviewee_job,
          interviewee_company: interviewData.interviewee_company,
        }}
        onSaveInterviewee={async (data) => {
          try {
            await updateResponseIntervieweeData(interviewData.id, data);
            setInterviewData(prev => ({ ...prev, ...data }));
          } catch (err) {
            captureException(err instanceof Error ? err : String(err), 'ResponseDetailView.saveIntervieweeFromMarketing', { sessionId, responseId: interviewData.id });
            setRecipeGuestError("Couldn't save guest details.");
          }
        }}
      />
    </div>
  );
  const transcriptLabel = !hasRecording ? 'Transcript' : hasVideoRecording ? 'Video & Transcript' : 'Audio & Transcript';

  return (
    <div className="container mx-auto space-y-4 px-4 py-6">
      {(navigation || hasCall) && <div className="flex min-w-0 items-center justify-between gap-2">
        {hasCall && <button type="button" aria-expanded={transcriptOpen} aria-controls="response-transcript"
          onClick={() => setTranscriptOpen(open => !open)}
          className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-surface-hover hover:text-foreground focus-visible:outline-ring">
          {transcriptLabel}
          <IconChevronDown aria-hidden size={16} className={`shrink-0 transition-transform motion-reduce:transition-none ${transcriptOpen ? 'rotate-180' : ''}`} />
        </button>}
        {navigation}
      </div>}
      {hasCall && <div id="response-transcript" className={transcriptOpen ? undefined : '!my-0'}>
        <AnimatedPresence show={transcriptOpen}>
          <VideoTranscriptViewer
            sessionId={sessionId}
            responseId={interviewData.id}
            videoEnabled={hasVideoRecording}
            recordingOptional={completion.multimodal}
            videoSegments={interviewData.video_segments}
            aiContext={reportData?.aiContext}
            conversationTurns={currentConversationTurns}
            timingRows={timingRows}
            sessionStartTime={new Date(session.created_at).getTime()}
            originalLanguage={getLanguageName(interviewData.original_language || 'en-US')}
            translatedTurns={interviewData.translated_turns}
            translatedLanguage={interviewData.translated_language ? getLanguageName(interviewData.translated_language) : null}
            showTranslated={showTranslated}
            onToggleTranslated={setShowTranslated}
            canTranslate={isCompleted && !interviewData.translated_turns && (interviewData.original_language || 'en-US') !== (session.output_language || 'en-US')}
            onTranslate={handleTranslate}
            translating={translating}
            translationError={translationError}
            onCopyTranscript={handleCopyTranscript}
            onDownloadTranscript={handleDownloadTranscript}
            copiedTranscript={copiedTranscript}
            scrollToTurnIndex={scrollToTurnIndex}
          />
        </AnimatedPresence>
      </div>}
      <ResponseOverviewLayout sidebar={<>
        {isCompleted && askBox}

        <ResponseGuestCard response={interviewData} canEditInterview={canEditInterview} onEdit={handleEditInterviewee} insights={reportData?.aiContext} />

        {showsConversationProgress && (
          <ConversationProgressCard
            progress={conversationProgress}
            unavailable={conversationProgressUnavailable}
            showLegacyUnavailable={hasUsefulResponseData}
            onEvidenceTurnSelect={hasCall ? handleNavigateToEvidenceTurn : undefined}
          />
        )}

        <ResponseDataCard response={interviewData} completion={completion} hasRecording={hasRecording} />
      </>}>
        {isInsufficient ? (
          <div className="bg-background/50 border border-border/50 rounded-2xl p-8">
            <Empty
              title="No credits left"
              message="This conversation could not start."
            />
          </div>
        ) : isCompleted ? (
          <InterviewReport
            interviewId={sessionId}
            responseId={interviewData.id}
            onNavigateToTranscript={hasCall ? handleNavigateToTranscript : undefined}
            savedReport={savedReport}
            recipeResults={recipes}
          >
            {responseResults}
          </InterviewReport>
        ) : (
          <div className="bg-background/50 border border-border/50 rounded-2xl p-8">
            <Empty
              title="Still in progress"
              message="The report will be available once it completes."
            />
          </div>
        )}
        {!isCompleted && recipes}
        {!isCompleted && responseResults}
      </ResponseOverviewLayout>

      {/* Edit interviewee modal */}
      <Modal open={editingInterviewee} onClose={() => { setEditingInterviewee(false); setSaveIntervieweeError(null); }} title="Edit Guest">
        <div className="space-y-3">
          <div>
            <label className="text-sm text-muted-foreground uppercase tracking-wider">Name</label>
            <input
              type="text"
              value={intervieweeForm.interviewee_name}
              onChange={e => setIntervieweeForm(f => ({ ...f, interviewee_name: e.target.value }))}
              className="w-full mt-1 px-3 py-2 bg-surface border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-volt"
              placeholder="Full name"
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground uppercase tracking-wider">Email</label>
            <input
              type="email"
              value={intervieweeForm.interviewee_email}
              onChange={e => setIntervieweeForm(f => ({ ...f, interviewee_email: e.target.value }))}
              className="w-full mt-1 px-3 py-2 bg-surface border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-volt"
              placeholder="email@example.com"
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground uppercase tracking-wider">Job Title</label>
            <input
              type="text"
              value={intervieweeForm.interviewee_job}
              onChange={e => setIntervieweeForm(f => ({ ...f, interviewee_job: e.target.value }))}
              className="w-full mt-1 px-3 py-2 bg-surface border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-volt"
              placeholder="Job title"
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground uppercase tracking-wider">Company</label>
            <input
              type="text"
              value={intervieweeForm.interviewee_company}
              onChange={e => setIntervieweeForm(f => ({ ...f, interviewee_company: e.target.value }))}
              className="w-full mt-1 px-3 py-2 bg-surface border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-volt"
              placeholder="Company name"
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground uppercase tracking-wider">LinkedIn</label>
            <input
              type="url"
              value={intervieweeForm.interviewee_linkedin}
              onChange={e => setIntervieweeForm(f => ({ ...f, interviewee_linkedin: e.target.value }))}
              className="w-full mt-1 px-3 py-2 bg-surface border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-volt"
              placeholder="https://linkedin.com/in/..."
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground uppercase tracking-wider">Company URL</label>
            <input
              type="url"
              value={intervieweeForm.interviewee_company_url}
              onChange={e => setIntervieweeForm(f => ({ ...f, interviewee_company_url: e.target.value }))}
              className="w-full mt-1 px-3 py-2 bg-surface border border-border rounded-lg text-foreground text-sm focus:outline-none focus:border-volt"
              placeholder="https://company.com"
            />
          </div>
          {saveIntervieweeError && (
            <p className="text-sm text-danger">{saveIntervieweeError}</p>
          )}
          <div className="flex gap-2 pt-2">
            <button aria-busy={savingInterviewee}
              onClick={handleSaveInterviewee}
              disabled={savingInterviewee}
              className="flex-1 py-2 button-accent text-sm font-bold rounded-lg transition-[background-color,color,opacity] duration-200 motion-reduce:transition-none disabled:opacity-50"
            >
              <ButtonContent loading={savingInterviewee}>Save</ButtonContent>
            </button>
            <button
              onClick={() => { setEditingInterviewee(false); setSaveIntervieweeError(null); }}
              className="flex-1 py-2 bg-surface text-muted-foreground text-sm font-bold rounded-lg hover:bg-surface-hover transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ResponseDetailView;


/** One overview grid owns both the loaded evidence and its loading state. */
function ResponseOverviewLayout({ children, sidebar }: { children: React.ReactNode; sidebar: React.ReactNode }) {
  return <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <div className="lg:col-span-2 min-w-0 flex flex-col gap-6">{children}</div>
    <div className="lg:col-span-1 lg:sticky lg:top-24 lg:self-start flex flex-col gap-6">{sidebar}</div>
  </div>;
}

/** The real guest card substitutes unknown values in its existing detail rows. */
function ResponseGuestCard({ response, canEditInterview = false, onEdit, insights }: {
  response: InterviewData | null;
  canEditInterview?: boolean;
  onEdit?: () => void;
  insights?: React.ComponentProps<typeof InsightText>['insights'];
}) {
  const guestEmail = response ? getResponseEmail(response) : undefined;
  const safeLinkedinUrl = safeHttpUrl(response?.interviewee_linkedin);
  const safeCompanyUrl = safeHttpUrl(response?.interviewee_company_url);
  return (<div data-response-card="guest" className="bg-background/50 border border-border/50 rounded-2xl p-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-foreground">Guest</h3>
                <Tooltip
                  content={canEditInterview ? '' : "You don't have permission to edit guest details."}
                  position="top"
                >
                  <button
                    onClick={onEdit}
                    disabled={!response || !canEditInterview}
                    aria-disabled={!response || !canEditInterview || undefined}
                    aria-label="Edit guest details"
                    className={`p-1.5 rounded-lg transition-colors ${
                      canEditInterview
                        ? 'text-muted-foreground hover:text-foreground hover:bg-surface-hover'
                        : 'text-subtle-foreground cursor-not-allowed'
                    }`}
                  >
                    <IconPencil className="w-4 h-4" />
                  </button>
                </Tooltip>
              </div>
              {!response || response.interviewee_name || guestEmail || response?.interviewee_job || response?.interviewee_company || response?.interviewee_linkedin || response?.interviewee_company_url || (response?.interviewee_metadata && Object.keys(response?.interviewee_metadata).length > 0) ? (
                <div className="space-y-3">
                  {(!response || response.interviewee_name) && (
                    <div>
                      <p className="text-sm text-muted-foreground uppercase tracking-wider">Name</p>
                      <p className="text-sm text-foreground font-medium">{response?.interviewee_name ?? <Skeleton width="9em" />}</p>
                    </div>
                  )}
                  {(!response || guestEmail) && (
                    <div>
                      <p className="text-sm text-muted-foreground uppercase tracking-wider">Email</p>
                      <p className="text-muted-foreground text-sm">{response ? <InsightText text={guestEmail ?? ''} insights={insights} /> : <Skeleton width="12em" />}</p>
                    </div>
                  )}
                  {response?.interviewee_job && (
                    <div>
                      <p className="text-sm text-muted-foreground uppercase tracking-wider">Job Title</p>
                      <p className="text-muted-foreground text-sm">{response?.interviewee_job}</p>
                    </div>
                  )}
                  {response?.interviewee_company && (
                    <div>
                      <p className="text-sm text-muted-foreground uppercase tracking-wider">Company</p>
                      <p className="text-muted-foreground text-sm"><InsightText text={response?.interviewee_company} insights={insights} /></p>
                    </div>
                  )}
                  {response?.interviewee_linkedin && (
                    <div>
                      <p className="text-sm text-muted-foreground uppercase tracking-wider">LinkedIn</p>
                      {/* Respondent-supplied value: only http(s) URLs render as links (S7) */}
                      {safeLinkedinUrl ? (
                        <a
                          href={safeLinkedinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-volt-soft text-sm hover:underline flex items-center gap-1"
                        >
                          <IconLink className="w-3.5 h-3.5" />
                          Profile
                        </a>
                      ) : (
                        <p className="text-muted-foreground text-sm break-all">{response?.interviewee_linkedin}</p>
                      )}
                    </div>
                  )}
                  {response?.interviewee_company_url && (
                    <div>
                      <p className="text-sm text-muted-foreground uppercase tracking-wider">Company URL</p>
                      {/* Respondent-supplied value: only http(s) URLs render as links (S7) */}
                      {safeCompanyUrl ? (
                        <a
                          href={safeCompanyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-volt-soft text-sm hover:underline"
                        >
                          {safeCompanyUrl}
                        </a>
                      ) : (
                        <p className="text-muted-foreground text-sm break-all">{response?.interviewee_company_url}</p>
                      )}
                    </div>
                  )}
                  {response?.interviewee_metadata && Object.keys(response?.interviewee_metadata).length > 0 && (
                    <div className="pt-2 border-t border-border/50">
                      <p className="text-sm text-muted-foreground uppercase tracking-wider mb-2">Metadata</p>
                      <div className="space-y-1.5">
                        {Object.entries(response?.interviewee_metadata).map(([key, value]) => (
                          <div key={key} className="flex justify-between items-center gap-2">
                            <span className="text-sm text-muted-foreground font-mono">{key}</span>
                            <span className="text-sm text-muted-foreground text-right truncate">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No guest details available.</p>
              )}
            </div>
);
}

/** Response metadata uses the same rows before and after its record arrives. */
function ResponseDataCard({ response, completion, hasRecording = false }: {
  response: InterviewData | null;
  completion?: ReturnType<typeof getResponseCompletion>;
  hasRecording?: boolean;
}) {
  return (<div data-response-card="data" className="bg-background/50 border border-border/50 rounded-2xl p-8">
              <h3 className="text-sm font-medium text-foreground mb-4">Response Data</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground uppercase tracking-wider">Status</span>
                  {response ? <StatusBadge type="response" status={getResponseDisplayStatus(response)} /> : <Skeleton width="5em" />}
                </div>
                {completion?.multimodal && (
                  <>
                    <div className="flex justify-between items-center gap-3">
                      <span className="text-sm text-muted-foreground uppercase tracking-wider">Completion</span>
                      <span className="text-sm text-foreground">{completion.submitted ? 'Submitted' : 'Not submitted'}</span>
                    </div>
                    {!hasRecording && <div className="flex justify-between items-center gap-3">
                      <span className="text-sm text-muted-foreground uppercase tracking-wider">Recording</span>
                      <span className="text-sm text-muted-foreground">Not recorded</span>
                    </div>}
                  </>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground uppercase tracking-wider">Created</span>
                  <span className="text-muted-foreground text-sm">{response ? formatDate(response.created_at) : <Skeleton width="8em" />}</span>
                </div>
                {response?.completed_at && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground uppercase tracking-wider">Completed</span>
                    <span className="text-muted-foreground text-sm">{formatDate(response?.completed_at)}</span>
                  </div>
                )}
                {response?.conversation_turns && response?.conversation_turns.length > 0 && (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground uppercase tracking-wider">Turns</span>
                      <span className="text-muted-foreground text-sm">{response?.conversation_turns.length}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground uppercase tracking-wider">Verified Quotes</span>
                      <span className="text-muted-foreground text-sm">{response?.verified_quotes.length}</span>
                    </div>
                  </>
                )}
                {response && getResponseActiveDuration(response) && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground uppercase tracking-wider">Duration</span>
                    <span className="text-muted-foreground text-sm">{response && getResponseActiveDuration(response)}</span>
                  </div>
                )}
              </div>
            </div>);
}

/** A pending response exposes its real overview without inventing response data or fetching reports. */
export function ResponseDetailView(props: LoadedResponseDetailViewProps | {
  loading: true;
  sessionId: string;
  navigation?: React.ReactNode;
}) {
  const loading = 'loading' in props && props.loading;
  return <section aria-label="Response" aria-busy={loading}>
    {'loading' in props ? <div className="container mx-auto space-y-4 px-4 py-6">
      {props.navigation && <div className="flex min-w-0 items-center">{props.navigation}</div>}
      <ResponseOverviewLayout sidebar={<>
        <ResponseGuestCard response={null} />
        <ResponseDataCard response={null} />
      </>}>
        <InterviewReport interviewId={props.sessionId} savedReport={{ reportData: null, loading: true }} />
      </ResponseOverviewLayout>
    </div> : <LoadedResponseDetailView key={props.response.id} {...props} />}
  </section>;
}
